/* eslint-disable no-nested-ternary */
/* eslint-disable no-eval */
/* eslint-disable no-unused-expressions */
import { AppBar, Grid, Tab, Tabs, Theme } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import React, { createContext, useEffect, useMemo, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoanStage } from "@toorak/tc-common-fe-sdk";
import { useParams } from "react-router-dom";
import { ToggleGroup } from "../../ui-components/ToggleGroup";
import { GuidelinesComparision } from "./GuidlinesComparision";
import { PricingComparision } from "./PricingComparision";
import { ObjectType } from "../../masterView/common";
import {
  getCriticalTests,
  getEligiblePartnerByLoanId,
  getEvaluationResultForPartner,
  getStaticPartnersResults,
  getTemplateData
} from "../../network/apiService";
import {
  showLoader,
  hideLoader
} from "../../stores/loaderAndException/loaderAndException.action";
import { RootState } from "../../stores/rootReducer";
import { URLParamsType } from "../../utils/constants";
import { evaluation } from "../evaluation-results/EvaluationHelper";
import {
  genFormattedUserDetails,
  getPropertiesWithFailedTest
} from "../../stores/EvaluationResults/EvaluationResults.action";
import { getLatestWaiverResponse } from "../../utils/utils";
import { aggregatePropertyData } from "../summary/NewFundingSource";
import { CreditLineLoanViewContext } from "../CreateLoan";

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: "flex",
      flexDirection: "column"
    },
    paper: { maxWidth: "60%" },
    sectionWrapper: {
      boxShadow: "0 2px 12px 0 rgba(50, 50, 93, 0.21)",
      padding: `12px 0`,
      display: "flex",
      backgroundColor: "#ffffff",
      marginTop: theme.spacing(3),
      borderRadius: 4
    },
    hidden: {
      background: "#fff"
    },
    headerButton: {
      backgroundColor: "#fff",
      border: "1px solid #dde1f3",
      borderRadius: "16px !important",
      padding: "2px",
      margin: "1rem 1.5rem"
    },
    button: {
      backgroundColor: "#fff",
      fontSize: "12px",
      fontWeight: 400,
      border: "0px solid #5e72e4 !important",
      letterSpacing: "0.5px",
      borderRadius: "16px !important",
      marginLeft: "0px !important",
      color: theme.palette.text.secondary,
      height: "auto",
      "&.$Mui-selected": {
        backgroundColor: "#5e72e4 !important",
        fontWeight: "600 !important",
        color: "#fff !important"
      },
      "&:disabled": {
        color: "#32325d",
        opacity: 0.5,
        fontWeight: 600,
        cursor: "no-drop",
        borderColor: "#d8d5d5 !important"
      },
      textTransform: "none"
    }
  })
);

const views = [
  { label: "Eligible Partners", value: "eligiblePartners" },
  { label: "Ineligible Partners", value: "ineligiblePartners" },
  { label: "Static Partners", value: "staticPartners" }
];

const subViews = [
  { label: "Pricing", value: "pricing" },
  { label: "Guideline Results", value: "guidelineTests" }
];

export const formatWaiverData = (
  waiver: ObjectType,
  customerInfo: ObjectType
) => {
  const { waiverResponses, waiverId } = waiver;
  let waiverStatus = "";
  if (waiverResponses?.length > 0) {
    const waiverResponseObject = getLatestWaiverResponse(waiverResponses);
    const waiverResponseType = waiverResponseObject.length
      ? waiverResponseObject[waiverResponseObject.length - 1].responseType
      : "Requested";
    waiverStatus = waiverResponseType;
  } else if (waiverId) {
    waiverStatus = "Requested";
  }

  let waiverUserInfo: ObjectType = {};
  if (waiverStatus === "Requested") {
    waiverUserInfo =
      genFormattedUserDetails(
        customerInfo,
        waiver.createdBy,
        waiver.createdOn
      ) || {};
  } else {
    const { length } = waiverResponses;
    waiverUserInfo =
      genFormattedUserDetails(
        customerInfo,
        waiverResponses[length - 1].responseBy,
        waiverResponses[length - 1].responseDate
      ) || {};
  }
  return {
    ...waiver,
    waiverStatus,
    ...waiverUserInfo
  };
};

export const getFlattenedData = (
  propertyDetails: any,
  loanData: any,
  evaluationResults: any,
  loanId: string
): ObjectType => {
  const {
    loanEconomics,
    loanInformation,
    borrowerInformation,
    guarantorInformation,
    loanSummary
  } = loanData;
  const borrowerCount =
    borrowerInformation?.filter(
      (borrowerInfoObj: ObjectType) =>
        borrowerInfoObj?.payload?.borrowerType === "Individual"
    )?.length ?? 0;
  const guarantorCount = guarantorInformation?.length ?? 0;
  const guarantorAndBorrowerCount = guarantorCount + borrowerCount;
  const aggregatedPropertyData = aggregatePropertyData(propertyDetails);

  let flatData: ObjectType = {
    loanEconomics,
    loanInformation
  };
  if (Object.keys(evaluationResults).length) {
    const {
      loanCharacterization,
      loanCharacterisations,
      loanFeatures,
      borrowerInfo,
      loanEconomics: loanResultEconomics,
      loanPricing,
      toorakProceeds
    } = evaluationResults?.loanResult?.[loanId];
    const loanCharactrerisation =
      loanCharacterization || loanCharacterisations || {};

    flatData = {
      ...flatData,
      loanCharactrerisation,
      loanFeatures,
      borrowerInfo,
      loanResultEconomics,
      loanSummary,
      loanPricing,
      toorakProceeds
    };
  }
  const transformedData: ObjectType = {};
  Object.keys(flatData)?.forEach((key: string) => {
    const obj = flatData?.[key] ?? {};
    Object.keys(obj)?.forEach((fieldId: string) => {
      transformedData[`${key}.${fieldId}`] = obj[fieldId];
    });
  });
  return {
    ...transformedData,
    ...aggregatedPropertyData,
    guarantorAndBorrowerCount
  };
};

const fetchCriticalTests = async (ruleVersion: string) => {
  try {
    const response: any = await getCriticalTests(ruleVersion)
    return response?.data?.criticalTestLoanEligibility ?? {}

  } catch (err) {
    console.error(err);
  }
}

// Shared helper function for processing evaluation results
export const processEvaluationResult = async (
  evalData: any,
  partnerId: string,
  loanId: string,
  loanStage: LoanStage,
  takeOutPartnerData: ObjectType | undefined,
  waivers: any,
  propertyDetails: any,
  loanData: any,
  criticalTests?: ObjectType
): Promise<{ partnersData: ObjectType; evaluation: any }> => {
  if (!takeOutPartnerData || !Object.keys(evalData?.loanResult?.[loanId] ?? {}).length) {
    return {
      partnersData: {
        label: takeOutPartnerData?.fullName ?? partnerId,
        id: partnerId,
        "data-testid": `guideline-and-pricing-data-${takeOutPartnerData?.fullName ?? partnerId}`,
        key: partnerId,
        creditLineEconomics: takeOutPartnerData?.creditLineEconomics?.find(
          (it: any) => it.isCurrentCycle
        ),
        data: {
          ...evalData?.loanResult?.[loanId]?.loanPricing,
          ...evalData?.loanResult?.[loanId]?.toorakProceeds
        },
        fail: [],
        pass: [],
        waived: [],
        errorAddressList: [],
        isSelected: false,
        failedCount: 0,
        criticalFailed: 0
      },
      evaluation: evalData
    };
  }

  const flattenedData = getFlattenedData(propertyDetails, loanData, evalData, loanId);

  const failTests: any[] = [];
  const passTests: any[] = [];
  const waivedTest: any[] = [];

  const MaxMinTests = { ...(evalData?.loanResult?.[loanId]?.maxMinParameters ?? {}) };
  const logicTests = { ...(evalData?.loanResult?.[loanId]?.logicTests ?? {}) };
  const finalCalculationTests = { ...(evalData?.loanResult?.[loanId]?.finalCalculation ?? {}) };

  const tests = { ...MaxMinTests, ...logicTests, ...finalCalculationTests };
  let criticalTestData: any = criticalTests?.[partnerId] ?? []
  if(!criticalTests?.length) {
    const ruleversion = evalData?.loanResult?.[loanId]?.ruleVersion?.RULE_VERSION || "";
    if(ruleversion) {
      criticalTestData = await fetchCriticalTests(ruleversion);
    }
  }
  const testNamesTemplateBody = Object.keys(tests).map(
    (testName: any) => `testNames.${testName}`
  );
  const maxMintestFailTemplateBody = Object.keys(MaxMinTests).map(
    (testName: any) => `loanResult.maxMinParameters.${testName}.message.FAIL`
  );
  const logictestFailTemplateBody = Object.keys(logicTests).map(
    (testName: any) => `loanResult.logicTests.${testName}.message.FAIL`
  );
  const finalCalculationFailTemplateBody = Object.keys(finalCalculationTests).map(
    (testName: any) => `loanResult.finalCalculation.${testName}.message.FAIL`
  );

  const reqBody = {
    templateKeys: [
      ...testNamesTemplateBody,
      ...maxMintestFailTemplateBody,
      ...logictestFailTemplateBody,
      ...finalCalculationFailTemplateBody
    ]
  };

  const templateRes: any = await getTemplateData(reqBody, partnerId);

  Object.keys(tests)?.forEach((testName: any) => {
    if (testName !== "sectionEligibility" && testName !== "propertyleveltest") {
      const testType = logicTests[testName]
        ? `logicTests`
        : MaxMinTests[testName]
        ? `maxMinParameters`
        : `finalCalculation`;

      const waiverDetails = {
        loanId,
        loanStage,
        ...(waivers?.get(`loanResult.${loanId}.${testType}.${testName}`) ?? {}),
        takeoutPartnerId: partnerId
      };
      const reqBody: any = {
          loanResult: {
            afterAdjustmentMaxLTVAsIs:
              flattenedData?.["toorakProceeds.afterAdjustmentMaxLTVAsIs"] ??
              flattenedData?.["toorakProceeds.toorakAdjustedAsIsLtv"],
            dscr: flattenedData?.["loanEconomics.dscr"] ?? null,
            logicTests
          }
      }
      if(evalData?.loanResult?.[loanId]?.testFailureMessages){
        Object.keys(evalData.loanResult[loanId].testFailureMessages).forEach((key)=>{
          reqBody.loanResult[key] = evalData.loanResult[loanId].testFailureMessages[key];
        });
      }
      const testFailReason = evaluation(
        templateRes?.data?.[`loanResult.${testType}.${testName}.message.FAIL`],
        reqBody
      );

      const testData = {
        label: eval(templateRes?.data?.[`testNames.${testName}`]) ?? testName,
        name: testName,
        type: testType,
        waiverDetails,
        isCriticalTest: criticalTestData.includes(testName) ?? false,
        reason:
          waiverDetails.waiverId || tests[testName] === "FAIL"
            ? testFailReason
            : "",
        loanData
      };

      switch (tests[testName]) {
        case "FAIL":
          failTests.push(testData);
          break;
        case "PASS":
          passTests.push(testData);
          break;
        case "WAIVED":
          waivedTest.push(testData);
          break;
      }
    }
  });

  const errorAddressList = getPropertiesWithFailedTest(evalData, propertyDetails);

  const partnersData = {
    label: takeOutPartnerData.fullName,
    id: partnerId,
    "data-testid": `guideline-and-pricing-data-${takeOutPartnerData.fullName}`,
    key: partnerId,
    creditLineEconomics: takeOutPartnerData.creditLineEconomics?.find(
      (it: any) => it.isCurrentCycle
    ),
    data: {
      ...evalData?.loanResult?.[loanId]?.loanPricing,
      ...evalData?.loanResult?.[loanId]?.toorakProceeds
    },
    fail: failTests,
    pass: passTests,
    waived: waivedTest,
    errorAddressList,
    isSelected: false,
    failedCount: failTests.length,
    criticalFailed: failTests.filter((it: any) => it.isCriticalTest).length ?? 0
  };

  return { partnersData, evaluation: evalData };
};

export const getPricingAndResults = (
  takeOutPartnerData: ObjectType,
  loanId: string,
  loanStage: LoanStage,
  selectedTakeOutId: string,
  waivers?: any,
  propertyDetails?: any,
  loanData?: any,
  rateLockPeriodAndExpiry?: any,
  criticalTests?: ObjectType
) =>
  new Promise((resolve, reject) => {
    getEvaluationResultForPartner(loanId, loanStage, takeOutPartnerData.id)
      .then(async (resultresp: any) => {
        const result = await processEvaluationResult(
          resultresp?.data,
          takeOutPartnerData.id,
          loanId,
          loanStage,
          takeOutPartnerData,
          waivers,
          propertyDetails,
          loanData,
          criticalTests
        );

        // Handle selectedTakeOutId
        result.partnersData.isSelected = takeOutPartnerData.id === selectedTakeOutId;

        // Handle rateLockPeriodAndExpiry
        if (
          rateLockPeriodAndExpiry &&
          takeOutPartnerData.id ===
            rateLockPeriodAndExpiry[Object.keys(rateLockPeriodAndExpiry)[0]]
              ?.takeoutPartnerId &&
          loanId === Object.keys(rateLockPeriodAndExpiry)[0]
        ) {
          result.partnersData.data = {
            ...result.partnersData.data,
            ...rateLockPeriodAndExpiry[Object.keys(rateLockPeriodAndExpiry)[0]]
          };
        } else {
          delete result.partnersData?.data?.rateLockAdjustment;
        }

        return resolve(result);
      })
      .catch((err) => {
        reject(err);
      });
  });

export const formatStaticPartnerResult = async (
  partnerId: string,
  partnerEvalData: any,
  loanId: string,
  loanStage: LoanStage,
  takeOutPartnerData: ObjectType | undefined,
  waivers: any,
  propertyDetails: any,
  loanData: any,
  criticalTests?: ObjectType,
  rateLockPeriodAndExpiry?: any
): Promise<{ partnersData: ObjectType; evaluation: any }> => {
  // partnerEvalData already has the correct structure: { loanResult: {...}, propertiesResults: {...} }
  const result = await processEvaluationResult(
    partnerEvalData,
    partnerId,
    loanId,
    loanStage,
    takeOutPartnerData,
    waivers,
    propertyDetails,
    loanData,
    criticalTests
  );

  // Handle rateLockPeriodAndExpiry
  if (
    rateLockPeriodAndExpiry &&
    partnerId ===
      rateLockPeriodAndExpiry[Object.keys(rateLockPeriodAndExpiry)[0]]
        ?.takeoutPartnerId &&
    loanId === Object.keys(rateLockPeriodAndExpiry)[0]
  ) {
    result.partnersData.data = {
      ...result.partnersData.data,
      ...rateLockPeriodAndExpiry[Object.keys(rateLockPeriodAndExpiry)[0]]
    };
  } else {
    delete result.partnersData?.data?.rateLockAdjustment;
  }

  return result;
};

export const GuidlinesComparisionContext = createContext<any>(undefined);

export const PricingAndGuidlinesSection = (props: {
  takOutPartnerList: ObjectType[];
  rateSheetType: string;
  loanId: string;
  programSelectConfig: any;
  takeOutPartner?: any;
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { loanStage } = useParams<URLParamsType>() as any;
  const {
    takOutPartnerList,
    rateSheetType,
    programSelectConfig,
    takeOutPartner,
    loanId
  } = props;
  const {
    isProgramSelectView,
    isLockPopUp,
    criticalTests,
    eligibleTakeOutPartnerResult
  } = programSelectConfig;
  const {
    thirtyYearLoanInformation,
    fesLoanInformation,
    thirtyYearLoanEconomics,
    bridgeLoanBorrowerInformation,
    bridgeLoanGuarantorInformation,
    loanRuleVersions,
    loanSummary,
    fesBorrowerInformation,
    fesLoanEconomics,
    propertyDetails,
    loanDetailId
  } = useSelector<RootState, any>(
    (state) => state.createLoanStore?.loanDetails
  );
  const { waiversCreated, customerInfo } = useSelector<RootState, any>(
    (state) => state.evaluationResultsStore
  );
  const [selectedView, setSelectedView] = useState(views[0].value);
  const [eligiblePartnersForExcel, setGligiblePartnersForExcel] = useState<
    any[]
  >([]);
  const [selectedSubView, setSelectedSubView] = useState(subViews[0].value);
  const [partnersData, setPartnersData] = useState<ObjectType>({
    eligiblePartners: [],
    ineligiblePartners: [],
    staticPartners: []
  });
  const [staticPartnersData, setStaticPartnersData] = useState<any[]>([]);
  const [staticPartnersRawResponse, setStaticPartnersRawResponse] = useState<any>(null);

  const { creditLines } = useContext(CreditLineLoanViewContext);

  useEffect(() => {
    if (!loanId) return;
    getEligiblePartnerByLoanId(loanId).then((res: any) => {
      const results = res.data.response.hits.hits;
      if (results.length) {
        setGligiblePartnersForExcel(
          results[0]._source.loan.eligibleTakeoutPartners
        );
      }
    });
  }, [loanId]);

  useEffect(() => {
    if (!loanId || !loanStage || !loanDetailId?.originatorPartyId) return;
    fetchStaticPartnersData(loanId, loanStage, loanDetailId?.originatorPartyId)
  },[loanId, loanStage, loanDetailId?.originatorPartyId]);

  const fetchStaticPartnersData = async (loanId: string, loanStage: LoanStage, originatorPartyId: string) => {
    try {
      const response: any = await getStaticPartnersResults(loanId, loanStage, originatorPartyId);
      setStaticPartnersRawResponse(response?.data);
    } catch (err) {
      console.error(err);
    }
  }

  const { eligiblePartners } = useMemo(() => {
    let eligiblePartnersList = [];
    if (eligibleTakeOutPartnerResult?.assignedPartner) {
      eligiblePartnersList = eligibleTakeOutPartnerResult.eligiblePartners.map(
        (partner: string) => ({
          takeoutPartnerPartyId: partner
        })
      );
    } else {
      eligiblePartnersList =
        loanStage === LoanStage.fes
          ? fesLoanInformation?.eligibleTakeoutPartners
          : thirtyYearLoanInformation?.payload?.eligibleTakeoutPartners ?? [];
    }
    if (loanStage === LoanStage.fes) {
      return {
        takeoutPartnerId:
          eligibleTakeOutPartnerResult?.assignedPartner ||
          fesLoanInformation?.takeoutPartnerId,
        eligiblePartners: eligiblePartnersList
      };
    }
    return {
      takeoutPartnerId:
        eligibleTakeOutPartnerResult?.assignedPartner ||
        thirtyYearLoanInformation?.payload?.takeoutPartnerId,
      eligiblePartners: eligiblePartnersList
    };
  }, [
    loanStage,
    fesLoanInformation,
    thirtyYearLoanInformation,
    eligibleTakeOutPartnerResult
  ]);

  const { rateLockExpiryDate, loanConfig } = useSelector<RootState, any>(
    (state) => state.createLoanStore.loanDetails
  );
  const [rateLockPeriodAndExpiry, setRateLockPeriodAndExpiry] =
    useState<any>(null);

  useEffect(() => {
    if (!takeOutPartner) return;
    setRateLockPeriodAndExpiry({
      [loanId]: {
        rateLockPeriod: loanConfig?.rateLockPeriod,
        rateLockExpiryDate,
        takeoutPartnerId: takeOutPartner?.id
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateLockExpiryDate, takeOutPartner?.id]);

  const partnerWiseWaivers = useMemo(() => {
    if (!waiversCreated.length) return {};
    const partnerWiseWaivers: any = {};
    waiversCreated?.forEach((waiver: any) => {
      const waiverData = formatWaiverData(waiver, customerInfo);
      if (!partnerWiseWaivers[waiver.takeoutPartnerId]) {
        partnerWiseWaivers[waiver.takeoutPartnerId] = new Map();
      }
      partnerWiseWaivers[waiver.takeoutPartnerId].set(
        waiver.waiverKeys[0],
        waiverData
      );
    });
    return partnerWiseWaivers;
  }, [waiversCreated, customerInfo]);

  const loanData: ObjectType = useMemo(() => {
    const fesLoanData = {
      borrowerInformation: [fesBorrowerInformation],
      loanInformation: fesLoanInformation,
      loanEconomics: fesLoanEconomics,
      loanRuleVersions
    };
    const prePostLoanData = {
      borrowerInformation: bridgeLoanBorrowerInformation,
      guarantorInformation: bridgeLoanGuarantorInformation,
      loanInformation: thirtyYearLoanInformation.payload,
      loanEconomics: thirtyYearLoanEconomics.payload,
      loanRuleVersions,
      loanSummary
    };

    if (loanStage === LoanStage.fes) {
      return fesLoanData;
    }
    return prePostLoanData;
  }, [
    loanStage,
    bridgeLoanBorrowerInformation,
    fesLoanInformation,
    bridgeLoanGuarantorInformation,
    fesBorrowerInformation,
    fesLoanEconomics,
    loanRuleVersions,
    loanSummary,
    thirtyYearLoanInformation,
    thirtyYearLoanEconomics
  ]);

  // Format static partners when raw response and dependencies are available
  useEffect(() => {
    if (
      !staticPartnersRawResponse?.partnersResults ||
      !loanId ||
      !loanStage ||
      !takOutPartnerList?.length ||
      !loanData
    ) return;

    const formatAllStaticPartners = async () => {
      dispatch(showLoader());
      try {
        const partnersResults = staticPartnersRawResponse.partnersResults;
        const formattedPartners: any[] = [];

        const formatPromises = Object.keys(partnersResults).map(async (partnerId) => {
          const takeOutPartnerData = creditLines.find((tp: any) => tp.id === partnerId);
          const formatted = await formatStaticPartnerResult(
            partnerId,
            partnersResults[partnerId],
            loanId,
            loanStage,
            takeOutPartnerData,
            partnerWiseWaivers?.[partnerId] ?? new Map(),
            propertyDetails,
            loanData,
            criticalTests,
            rateLockPeriodAndExpiry
          );
          return formatted.partnersData;
        });

        const results = await Promise.all(formatPromises);
        formattedPartners.push(...results);
        setStaticPartnersData(formattedPartners);
      } catch (err) {
        console.error("Error formatting static partners:", err);
      } finally {
        dispatch(hideLoader());
      }
    };

    formatAllStaticPartners();
  }, [
    staticPartnersRawResponse,
    loanId,
    loanStage,
    takOutPartnerList,
    partnerWiseWaivers,
    propertyDetails,
    loanData,
    criticalTests,
    rateLockPeriodAndExpiry,
    dispatch
  ]);

  useEffect(() => {
    if (!partnerWiseWaivers) return;
    const eligiblePartnersList: any[] = [];
    const inEligiblePartnersList: any[] = [];
    const excelData = sessionStorage.getItem("excelUpload");
    const requiredEligiblePartners = excelData
      ? eligiblePartnersForExcel ?? eligiblePartners
      : eligiblePartners;
    takOutPartnerList?.forEach((tpData: any) => {
      if (
        /**
         * eligiblePartners have to changed to get data for bulk upload
         */
        requiredEligiblePartners?.some(
          (eligibleTp: ObjectType) =>
            tpData.id === eligibleTp.takeoutPartnerPartyId
        )
      ) {
        eligiblePartnersList.push(tpData);
      } else {
        inEligiblePartnersList.push(tpData);
      }
    });

    const partnersData: ObjectType = {
      eligiblePartners: eligiblePartnersList,
      ineligiblePartners: inEligiblePartnersList,
      staticPartners: staticPartnersData
    };
    setPartnersData(partnersData);

    // Static partners are already formatted, no need to call getPricingAndResults
    if (selectedView === "staticPartners") {
      return;
    }

    if (!partnersData?.[selectedView]?.length) return;
    dispatch(showLoader());
    const resultsPromiseList: any[] = [];
    partnersData?.[selectedView]?.forEach((item: any) => {
      resultsPromiseList.push(
        getPricingAndResults(
          item,
          loanId,
          loanStage,
          item.id,
          partnerWiseWaivers?.[item.id] ?? new Map(),
          propertyDetails,
          loanData,
          rateLockPeriodAndExpiry,
          criticalTests
        )
      );
    });

    // let tempEvaluationResult: any = {};
    Promise.all(resultsPromiseList)
      .then((res: any) => {
        // const evaluationResult: any = res?.find(
        //   (item: any) => item?.evaluation
        // );
        // if (
        //   Object.keys(evaluationResult ?? {}).length &&
        //   Object.keys(tempEvaluationResult)?.length === 0
        // ) {
        //   tempEvaluationResult = evaluationResult;
        //   if (Object.keys(tempEvaluationResult)?.length)
        //     dispatch(
        //       evaluateLoanSuccess({
        //         loanEvaluationResult: tempEvaluationResult ?? {},
        //         reasonForFail: {},
        //         errorAddressList: {},
        //         isSubmitted: false
        //       })
        //     );
        // }
        const tempPartnersData: any = [];
        res?.map((item: any) =>
          tempPartnersData.push(item?.partnersData ?? item)
        );
        setPartnersData({
          ...partnersData,
          [selectedView]: tempPartnersData
        });
        dispatch(hideLoader());
      })
      .catch((err) => {
        dispatch(hideLoader());
        console.error(err);
      });
  }, [
    loanId,
    loanStage,
    dispatch,
    selectedView,
    takOutPartnerList,
    eligiblePartners,
    eligiblePartnersForExcel,
    partnerWiseWaivers,
    loanData,
    propertyDetails,
    rateLockPeriodAndExpiry,
    criticalTests,
    staticPartnersData
  ]);

  const handleChange = (e: any, val: string) => {
    if (!val) return;
    setSelectedView(val);
    setSelectedSubView(subViews[0].value);
  };
  const handleToggle = (e: any, val: string) => {
    if (!val) return;
    setSelectedSubView(val);
  };

  useEffect(() => {
    if (isProgramSelectView) {
      setSelectedView(views[1].value);
    }
  }, [isProgramSelectView]);

  return (
    <GuidlinesComparisionContext.Provider
      value={{
        partnersData: partnersData?.[selectedView],
        // getPricingAndResults,
        // setPartnersData,
        rateSheetType,
        takeOutPartner
      }}
    >
      {isLockPopUp ? (
        <PricingComparision
          programSelectConfig={programSelectConfig}
          partnersData={partnersData?.eligiblePartners ?? []}
        />
      ) : isProgramSelectView ? (
        <GuidelinesComparision
          showSummary
          programSelectConfig={programSelectConfig}
        />
      ) : (
        <Grid
          container
          className={classes.sectionWrapper}
          style={{ flexDirection: "column" }}
        >
          <AppBar
            position="static"
            color="default"
            style={{
              padding: "0px 24px",
              borderBottom: "1px solid #dedede",
              backgroundColor: "transparent",
              backgroundImage: "none",
              zIndex: 1
            }}
          >
            <Tabs
              value={selectedView}
              onChange={handleChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="partner classification"
              className="loans-tab-container"
            >
              {views.map((item: any, idx) => (
                <Tab
                  key={item.value}
                  label={`${item.label} (${
                    idx === 0
                      ? partnersData.eligiblePartners.length
                      : idx === 1
                        ? partnersData.ineligiblePartners.length
                        : partnersData.staticPartners.length
                  })`}
                  data-testid={`eligible-and-non-eligible-partner-${idx}`}
                  value={item.value}
                  style={{ textTransform: "none", fontSize: "14px" }}
                />
              ))}
            </Tabs>
          </AppBar>
          {["eligiblePartners", "staticPartners"].includes(selectedView) && (
            <>
              <Grid style={{ marginTop: "1rem" }}>
                <ToggleGroup
                  toggleOptions={subViews}
                  handleChange={handleToggle}
                  alignment={selectedSubView}
                  classes={classes}
                />
              </Grid>
              {selectedSubView === "pricing" && (
                <PricingComparision
                  programSelectConfig={programSelectConfig}
                  partnersData={partnersData?.[selectedView] ?? []}
                />
              )}
              {selectedSubView === "guidelineTests" && (
                <GuidelinesComparision
                  showSummary
                  programSelectConfig={{ isProgramSelectView: false }}
                />
              )}
            </>
          )}
          {selectedView === "ineligiblePartners" && (
            <GuidelinesComparision
              showSummary
              programSelectConfig={{ isProgramSelectView: false }}
            />
          )}
        </Grid>
      )}
    </GuidlinesComparisionContext.Provider>
  );
};
